import { useMemo } from "react"

import { createGlobalStore } from "@/stores/global"
import { usePositionsQuery } from "@/hooks/api/usePositionQuery"

/**
 * 포지션(세부) 정렬 순번. 전역 positionList(= ordering=position, 등번호 순)의 순서를 기준으로 쓴다.
 * 테이블 포지션(depth 2) 클릭 정렬·선수 목록 정렬용. 목록에 없는 코드는 맨 뒤.
 * (순서는 백엔드 단일 출처를 신뢰 — 프론트 하드코딩 순서표 없음)
 */
export const usePositionRank = () => {
  const positionList = createGlobalStore((state) => state.positionList)

  return useMemo(() => {
    const rankMap = new Map(positionList.map((position, index) => [position.code, index]))

    return (code?: string | number) => rankMap.get(String(code ?? "")) ?? Number.MAX_SAFE_INTEGER
  }, [positionList])
}

/**
 * 포지션 그룹(depth 1, 예: B3/CE) 정렬 순번. 그룹 클러스터 순서(ordering=group)로 받은 목록에서
 * 그룹이 처음 등장하는 순서를 그룹 순서(GROUP_ORDER)로 본다. 미존재 그룹은 맨 뒤.
 * (등번호 순 목록의 첫 등장으로 추론하면 그룹 순서가 틀어지므로 반드시 group 목록을 소스로 사용)
 */
export const usePositionGroupRank = () => {
  const { data: groupedPositionList } = usePositionsQuery("group")

  return useMemo(() => {
    const groupRankMap = new Map<string, number>()
    ;(groupedPositionList ?? []).forEach((position) => {
      if (position.group && !groupRankMap.has(position.group)) {
        groupRankMap.set(position.group, groupRankMap.size)
      }
    })

    return (group?: string) => groupRankMap.get(group ?? "") ?? Number.MAX_SAFE_INTEGER
  }, [groupedPositionList])
}
